import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/camera-guided-portable-tms — PROMPT 8
 * Ranks for the descriptive terms (portable TMS, camera-guided TMS, TMS
 * neuronavigation, 3-minute TMS) without the brand. Ampa One is the
 * camera-guided, portable system we use. Clinic-portable, not at-home.
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "What is TMS neuronavigation?",
    answer:
      "TMS neuronavigation is any method that tracks where the coil sits on your head so treatment stays on the same target session after session. Camera-guided neuronavigation uses a camera (on the coil or in the room) plus a reference cap to track the coil's position live. It is more precise than manual head measurements and is different from MRI-guided targeting, which uses a brain scan to choose the target in the first place.",
  },
  {
    question: "Is there portable TMS I can use at home?",
    answer:
      "No. Ampa One is clinic-portable — it fits in two wheeled cases and runs on a standard outlet — but it is used in the clinic under the supervision of a trained technician. It is not an at-home device. TMS requires motor-threshold mapping, physician oversight, and safety screening, which cannot be done at home.",
  },
  {
    question: "How long is a TMS session with Ampa One?",
    answer:
      "An Ampa One session is a few minutes of stimulation — about a minute to set up, then a few minutes of treatment. That is far shorter than the 19–37 minutes of a conventional TMS session, because Ampa One uses theta-burst stimulation patterns. You can drive home and return to work afterward.",
  },
  {
    question: "What is a 3-minute TMS session?",
    answer:
      "A '3-minute TMS session' refers to theta-burst stimulation (TBS), which delivers the same number of pulses as a longer conventional session in a fraction of the time by using a faster burst pattern. Ampa One uses TBS, so a stimulation session can take just a few minutes. The short session is a feature of the stimulation pattern, not a lower dose.",
  },
];

export const pageCameraGuidedPortableTms = makeArticle({
  slug: "camera-guided-portable-tms",
  title:
    "Camera-Guided & Portable TMS: How Neuronavigation Keeps Treatment On Target",
  description:
    "What camera-guided TMS is, how neuronavigation keeps the coil on target, what 'portable' TMS means, and how 3-minute theta-burst sessions work. Ampa One is the system we use.",
  canonical: "/learn/camera-guided-portable-tms",
  h1: "What Is Camera-Guided TMS? Portable, Precise, Minutes-Long Sessions",
  breadcrumbLabel: "Camera-Guided & Portable TMS",
  shortAnswer:
    "Camera-guided TMS uses a camera on the coil and a pre-printed cap to track where the coil sits on your head during every session, so treatment stays on the same target day after day. It is different from MRI-guided targeting (used by SAINT®), which uses a brain scan to choose a personalized target. Ampa One is the camera-guided, portable TMS system we use. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "Camera-guided TMS tracks the coil's position live with a camera plus a pre-printed cap.",
    "It is different from MRI-guided targeting (SAINT®), which uses a brain scan to choose the target.",
    "'Portable' means clinic-portable — fits in two cases, runs on a standard outlet — not an at-home device.",
    "Theta-burst stimulation (TBS) makes sessions minutes-long instead of 19–37 minutes.",
    "Ampa One is the camera-guided, portable TMS system we use.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        "Camera-guided TMS uses a camera built into the coil and a pre-printed neuronavigation cap to track where the coil sits on your head during every session, so treatment stays on the same target day after day. It is different from MRI-guided targeting (used by SAINT®), which uses a brain scan to choose a personalized target. Ampa One is the camera-guided, portable TMS system we use.",
      ],
    },
    {
      heading: "Three ways TMS finds its target",
      paragraphs: [
        "TMS systems find their target in three main ways, from simplest to most personalized:",
      ],
      table: {
        headers: ["Method", "How it works", "Example"],
        rows: [
          [
            "Head measurements",
            "Manual measurements from landmarks on your skull to estimate the target",
            "Conventional TMS (NeuroStar, MagVenture)",
          ],
          [
            "Camera neuronavigation",
            "A camera plus a pre-printed cap tracks the coil's position live each session",
            "Ampa One",
          ],
          [
            "MRI / fMRI targeting",
            "A brain scan personalizes the target to your individual brain anatomy",
            "SAINT® (fMRI)",
          ],
        ],
      },
    },
    {
      heading: "Why coil drift matters",
      paragraphs: [
        "Patients move during treatment, and coils shift. With manual measurements, the coil can drift off the original target over a multi-week course, which can reduce how much stimulation reaches the intended area. Camera-guided neuronavigation tracks the coil live, and Ampa One's Target Lock (introduced October 2026) automatically pauses stimulation if the coil moves off target and helps the technician realign — so every session stays on the same target.",
      ],
    },
    {
      heading: "Portable TMS: what 'portable' means",
      paragraphs: [
        "Ampa One is clinic-portable: it fits in two wheeled cases and runs on a standard wall outlet, so it can be set up in an exam room without the heavy mechanical arm of a conventional system. This is not an at-home device — TMS requires motor-threshold mapping, physician oversight, and safety screening, which are done in the clinic under supervision.",
      ],
    },
    {
      heading: "From 38-minute to 3-minute sessions",
      paragraphs: [
        "Conventional TMS sessions run 19–37 minutes because they deliver pulses one at a time at 10 Hz. Theta-burst stimulation (TBS) delivers the same number of pulses in a fraction of the time by using a faster burst pattern, so an Ampa One stimulation session can take just a few minutes. The short session is a feature of the stimulation pattern, not a lower dose. See [[accelerated TMS & theta burst|/learn/accelerated-tms-theta-burst-stimulation]].",
      ],
    },
    {
      heading: "Is camera-guided TMS as good as MRI-guided?",
      paragraphs: [
        "They solve different problems. MRI-guided targeting (SAINT®) uses a brain scan to choose a personalized target — which is more involved and requires an MRI. Camera-guided targeting (Ampa One) keeps the coil on a chosen target session after session, with short sessions and no MRI needed, and supports accelerated schedules. No head-to-head trial compares them. If you cannot get an MRI, camera-guided TMS may be the better fit. See [[SAINT® vs Ampa One|/learn/saint-vs-ampa-one]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Ampa Inc. Ampa One — camera-guided, portable TMS system; Target Lock (October 2026).",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "SAINT® vs Ampa One",
      href: "/learn/saint-vs-ampa-one",
      description: "Camera-guided vs fMRI-guided targeting",
    },
    {
      title: "Ampa One vs NeuroStar vs BrainsWay",
      href: "/learn/ampa-one-vs-neurostar-vs-brainsway",
      description: "Compare TMS devices",
    },
    {
      title: "Accelerated TMS & Theta Burst",
      href: "/learn/accelerated-tms-theta-burst-stimulation",
      description: "How theta-burst stimulation shortens sessions",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    {
      label: "Three ways TMS targets",
      href: "#three-ways-tms-finds-its-target",
    },
    { label: "Coil drift", href: "#why-coil-drift-matters" },
    { label: "Portable TMS", href: "#portable-tms-what-portable-means" },
    {
      label: "3-minute sessions",
      href: "#from-38-minute-to-3-minute-sessions",
    },
    {
      label: "Camera vs MRI",
      href: "#is-camera-guided-tms-as-good-as-mri-guided",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. Common side effects include mild scalp discomfort and headache that usually fade. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "See if camera-guided TMS is a fit, free",
    primaryText: "Request a free consultation",
    primaryHref: "/ampa",
    secondaryText: "Call 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
